import { useCallback, useEffect, useState } from 'react';
import {
  getContractBalancesRequest,
  makePaymentReset,
  getPaymentTypeReset,
  getPaymentTypeRequest,
} from 'actions/accountActions';
import { getEnrolmentStatus } from 'actions/accountingIntegrationActions';
import { getEverydayRewardsEnrolmentStatus } from 'actions/everydayRewardsActions';
import { useDispatch, useSelector } from 'react-redux';

import './account-detail-modal.scss';
import AccountDetail from 'features/accounts/components/account-detail/account-detail';
import AccountAddressList from 'features/accounts/components/account-address-list/account-address-list';
import TransactionList from 'features/cards/components/transaction-list/transaction-list';
import CardsListPage from 'features/cards/pages/cards-list-page/cards-list-page';
import EverydayRewards from 'components/everyday-rewards/everyday-rewards';
import AccountingIntegration from 'components/accounting-integration/accounting-integration';
import AccountPayment from 'components/account-payment/account-payment';
import AccountFavouriteEditForm from 'features/cards/components/new-card-modal/edit-favs/AccountFavouriteEditForm';
import { Tabs } from '@ampol/design-system';
import TabWrapper from 'components/tab-wrapper/TabWrapper';
import { useGetAccountElegibillity } from './useGetAccountElegibillity';
import { removeAccountPaymentButton, setAccountPaymentCloseButton } from 'actions/modalMetaActions';
/**
 * Account modal
 * @param account Account that the modal is presenting information for
 */
const AccountDetailModal = ({ account, onClose }) => {
  const { contractNumber, level } = account;
  const secondaryActionButton = useSelector((state:any) => state.modalStore.secondaryActionButton);
  const dispatch = useDispatch();
  const portalType = useSelector((state: any) => state.userStore.portalType);
  const assignedGrandfatherClientNumber = useSelector(
    (state: any) => state.userStore.assignedGrandfatherClientNumber
  );
  const balance = useSelector(
    (state: any) =>
      state.accountsStore.accounts.find(
        (acc) => acc.contractNumber === contractNumber
      )?.balance
  );
  
  const removePaymentButtonCallback = useCallback(() => {
    dispatch(removeAccountPaymentButton());
  }, []);

  const {
    isAccountingEligible,
    isPaymentsEligible,
    isPaymentsUpdateEligible,
    isEverydayRewardsEligible,
  } = useGetAccountElegibillity();

  useEffect(() => {
    if (portalType === 'card') {
      dispatch(makePaymentReset());
      dispatch(getPaymentTypeReset());
      dispatch(
        getPaymentTypeRequest(assignedGrandfatherClientNumber, contractNumber)
      );
      dispatch(
        getEnrolmentStatus(assignedGrandfatherClientNumber, contractNumber)
      );
      dispatch(
        getEverydayRewardsEnrolmentStatus(
          assignedGrandfatherClientNumber,
          contractNumber
        )
      );
    }
    dispatch(
      getContractBalancesRequest(
        contractNumber,
        assignedGrandfatherClientNumber
      )
    );

    dispatch(setAccountPaymentCloseButton({
      ...secondaryActionButton,
      onClick: () => {
        onClose();
        if (currentTab.id === 'payments') {
          window.dataLayer.push({
            event: 'Card_Portal_Accounts_Make_A_Payment_Close',
            close: true,
          });
        }
      }
    }));
  }, []);

  const tabs = [
    {
      id: 'account-detail',
      label: 'Account',
      hasAccess: true,
      children: (
        <TabWrapper>
          <AccountDetail account={account} balance={balance} />
        </TabWrapper>
      ),
    },
    {
      id: 'address',
      label: 'Address',
      hasAccess: true,
      children: (
        <TabWrapper>
          <AccountAddressList contractNumber={contractNumber} />
        </TabWrapper>
      ),
    },
    {
      id: 'transactions',
      label: 'Transactions',
      hasAccess: portalType === 'card',
      children: (
        <TabWrapper>
          <TransactionList
            accountId={contractNumber}
            modalType={'account'}
            accountLevel={level}
          />
        </TabWrapper>
      ),
    },
    {
      id: 'cards',
      label: 'Cards',
      hasAccess: portalType === 'card',
      children: (
        <TabWrapper>
          <CardsListPage
            modalType={'account'}
            contractNumber={contractNumber}
            accountLevel={level}
            includeTabs={false}
          />
        </TabWrapper>
      ),
    },
    {
      id: 'accountcardusageoptions',
      label: 'Favourites',
      hasAccess: portalType === 'card',
      children: (
        <TabWrapper>
          <AccountFavouriteEditForm contractNumber={contractNumber} modalType={'accountFavorites'}/>
        </TabWrapper>
      ),
    },
    {
      id: 'everyday-rewards',
      label: 'Everyday Rewards',
      hasAccess: isEverydayRewardsEligible,
      children: (
        <TabWrapper>
          <EverydayRewards accountId={contractNumber} />
        </TabWrapper>
      ),
    },
    {
      id: 'accounting-integration',
      label: 'Accounting',
      hasAccess: isAccountingEligible,
      children: (
        <TabWrapper>
          <AccountingIntegration accountId={contractNumber} />
        </TabWrapper>
      ),
    },
    {
      id: 'payments-update',
      label: 'Payment details',
      hasAccess: isPaymentsUpdateEligible,
      children: (
        <TabWrapper>
          <AccountPayment
            accountNumber={contractNumber}
            accountBalance={balance}
            isUpdatePaymentsPage={true}
            removePaymentButtonCallback={removePaymentButtonCallback}
          />
        </TabWrapper>
      ),
    },
    {
      id: 'payments',
      label: 'Make a payment',
      hasAccess: isPaymentsEligible,
      children: (
        <TabWrapper>
          <AccountPayment
            accountNumber={contractNumber}
            accountBalance={balance}
            removePaymentButtonCallback={removePaymentButtonCallback}
          />
        </TabWrapper>
      ),
    }
  ];

  const [currentTab, setCurrentTab] = useState(tabs[0]);

  const handleTabClick = (tabItem) => {
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Navigation',
      nav_item: tabItem?.label
    });
    setCurrentTab(tabItem);
  };

  return (
    <div className="w-full -mt-[35px]">
      <Tabs
        items={tabs}
        currentTab={currentTab}
        onTabSelected={handleTabClick}
        className="w-full"
        isSticky
      />
    </div>
  );
};

export default AccountDetailModal;
